import { useState, useEffect } from "react";
import Pagination from "../../Component/Common/Pagination";
import RefreshButton from "../../Component/Common/RefreshButton";
import RegularLoader from "../../Component/Loader/RegularLoader";
import { Eye, Pencil, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import formatCurrency from "../../Component/EmployeeComponents/formatCurrency";
import CreatePackage from "./CreatePackage";
import Swal from "sweetalert2";
import { BASE_URL } from "../../config/api";

export default function PackageTable() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 7;

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}packages`);
      const data = await res.json();
      if (!res.ok) throw new Error("Failed to fetch packages");
      setPackages(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  // 🚀 DELETE FUNCTION
  const handleDelete = async (pkgId) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await fetch(`${BASE_URL}packages/${pkgId}`, {
        method: "DELETE",
      });

      if (!res.ok) throw new Error("Failed to delete package");

      toast.success("Package deleted successfully!");
      fetchPackages();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const totalPages = Math.ceil(packages.length / pageSize);

  const paginatedPackages = packages.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4 flex flex-col h-full">

      <div className="flex items-center justify-end mb-4 gap-2">
        {/* Mobile Add Button */}
        <div className="md:hidden">
          <button
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-1 rounded-xl bg-blue-600 text-white text-sm font-semibold shadow hover:bg-blue-700"
          >
            + 
          </button>
        </div>

        {/* Refresh Button */}
        <RefreshButton onClick={fetchPackages} />
      </div>


      <div className="hidden md:block overflow-x-auto -mx-4 md:mx-0">
        <table className="min-w-full text-left text-xs md:text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50">
              <th className="py-2 px-2">Package Name</th>
              <th className="px-2">Badge</th>
              <th className="px-2">Benefits</th>
              <th className="px-2">Validity</th>
              <th className="px-2">Amount</th>
              <th className="px-2">Created</th>
              <th className="px-2 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="py-6 text-center">
                  <RegularLoader />
                </td>
              </tr>
            ) : paginatedPackages.length === 0 ? (
              <tr>
                <td colSpan="7" className="py-6 text-center text-slate-400">
                  No packages found.
                </td>
              </tr>
            ) : (
              paginatedPackages.map((pkg) => {
                const createdDate = new Date(pkg.created_at).toLocaleDateString("en-IN");

                return (
                  <tr key={pkg.id} className="border-b border-slate-50 last:border-0">
                    <td className="px-2 py-2">{pkg.package_name}</td>
                    <td className="px-2">{pkg.badge?.name || "-"}</td>
                    <td className="px-2 text-blue-600 font-semibold">{pkg.badge?.benefits}%</td>
                    <td className="px-2">{pkg.validity_days} Days</td>
                    <td className="px-2 font-semibold">{formatCurrency(pkg.amount)}</td>
                    <td className="px-2">{createdDate}</td>

                    <td className="px-2 flex items-center justify-center gap-3">
                      <button className="text-blue-600 hover:text-blue-800">
                        <Eye size={16} />
                      </button>

                      <button
                        className="text-amber-600 hover:text-amber-800"
                        onClick={() => {
                          setSelectedPackage(pkg);
                          setShowModal(true);
                        }}
                      >
                        <Pencil size={16} />
                      </button>

                      {/* 🗑 DELETE BUTTON */}
                      <button
                        className="text-red-600 hover:text-red-800"
                        onClick={() => handleDelete(pkg.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>


      {/* ================= MOBILE CARDS ================= */}
      <div className="md:hidden space-y-5">
        {loading ? (
          <div className="flex justify-center py-8">
            <RegularLoader />
          </div>
        ) : paginatedPackages.length === 0 ? (
          <div className="text-center text-sm text-slate-400 py-8">
            No packages found.
          </div>
        ) : (
          paginatedPackages.map((pkg) => {
            const createdDate = new Date(pkg.created_at).toLocaleDateString("en-IN");

            return (
              <div
                key={pkg.id}
                className="relative bg-white rounded-3xl shadow-lg border border-slate-100 p-5"
              >
                {/* Header */}
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">
                      {pkg.package_name}
                    </h3>
                    <p className="text-xs text-slate-400 mt-1">
                      {createdDate}
                    </p>
                  </div>

                  <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                    {pkg.validity_days} Days
                  </span>
                </div>

                {/* Divider */}
                <div className="my-4 border-t border-slate-100" />

                {/* Details */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="text-slate-400">Badge</p>
                    <p className="font-semibold text-slate-800">
                      {pkg.badge?.name || "-"}
                    </p>
                  </div>

                  <div>
                    <p className="text-slate-400">Benefits</p>
                    <p className="font-semibold text-blue-600">
                      {pkg.badge?.benefits}% 
                    </p>
                  </div>

                  <div>
                    <p className="text-slate-400">Amount</p>
                    <p className="font-semibold text-slate-900">
                      {formatCurrency(pkg.amount)}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-5 flex justify-end gap-3">
                  <button className="w-9 h-9 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
                    <Eye size={16} />
                  </button>

                  <button
                    onClick={() => {
                      setSelectedPackage(pkg);
                      setShowModal(true);
                    }}
                    className="w-9 h-9 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center"
                  >
                    <Pencil size={16} />
                  </button>

                  <button
                    onClick={() => handleDelete(pkg.id)}
                    className="w-9 h-9 rounded-xl bg-red-100 text-red-600 flex items-center justify-center"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>


      {totalPages > 1 && (
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-4 w-[400px]">
            <CreatePackage
              mode="edit"
              packageId={selectedPackage.id}
              packageData={selectedPackage}
              onSubmit={() => fetchPackages()}
              onClose={() => setShowModal(false)}
            />
          </div>
        </div>
      )}

      {createOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4 md:hidden">
          <div className="bg-white rounded-xl p-4 w-full max-w-md">
            <CreatePackage
              mode="create"
              onSubmit={() => {
                fetchPackages();
                setCreateOpen(false);
              }}
              onClose={() => setCreateOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}




// import { useState, useEffect, useMemo } from "react";
// import Pagination from "../../Component/Common/Pagination";
// import RefreshButton from "../../Component/Common/RefreshButton";
// import BlueButton from "../../Component/Common/BlueButton";
// import RegularLoader from "../../Component/Loader/RegularLoader";
// import { STATUSES } from "../../Component/EmployeeComponents/STATUSES";
// import { monthLabels } from "../../Component/EmployeeComponents/monthLabels";
// import formatCurrency from "../../Component/EmployeeComponents/formatCurrency";
// import LocationFilter from "../../Component/Common/LocationFilter";
// import DateRangePicker from "../../Component/Common/DateRangePicker";
// import { Eye, Pencil } from "lucide-react";
// import toast from "react-hot-toast";


// export default function PackageTable() {
//   const [leads, setLeads] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);

//   const [currentPage, setCurrentPage] = useState(1);
//   const [startDate, setStartDate] = useState(null);
//   const [endDate, setEndDate] = useState(null);
//   const [selectedLocation, setSelectedLocation] = useState("");
//   const [filterStatus, setFilterStatus] = useState("All");

//   const [selectedLeadForView, setSelectedLeadForView] = useState(null);
//   const [viewModalOpen, setViewModalOpen] = useState(false);

//   const [selectedLead, setSelectedLead] = useState(null);
//   const [editModalOpen, setEditModalOpen] = useState(false);

//   const pageSize = 7;

//   const fetchLeads = async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       const res = await fetch("${BASE_URL}/warranty/wlead/list");
//       const data = await res.json();

//       if (!res.ok || !data.status) {
//         throw new Error(data.message || "Failed to fetch leads");
//       }

//       const mapped = data.data.map((lead) => ({
//         id: lead.id,
//         name: lead.name,
//         owner: lead.owner_name || "-",
//         location: lead.district || lead.state || "-",
//         type: lead.lead_type || "-",
//         status: lead.status,
//         value: lead.lead_amount || 0,
//         createdAt: lead.created_at,
//         full: lead,
//       }));

//       setLeads(mapped);
//     } catch (err) {
//       toast.error(err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchLeads();
//   }, []);

//   const uniqueLocations = [...new Set(leads.map((l) => l.location))];

//   const filteredLeads = useMemo(() => {
//     return leads
//       .filter((lead) => (filterStatus === "All" ? true : lead.status === filterStatus))
//       .filter((lead) => {
//         if (!startDate || !endDate) return true;
//         const created = new Date(lead.createdAt);
//         return created >= startDate && created <= endDate;
//       })
//       .filter((lead) => (selectedLocation ? lead.location === selectedLocation : true));
//   }, [leads, filterStatus, startDate, endDate, selectedLocation]);

//   const totalPages = Math.ceil(filteredLeads.length / pageSize);

//   const paginatedLeads = filteredLeads.slice(
//     (currentPage - 1) * pageSize,
//     currentPage * pageSize
//   );

//   const resetFilters = () => {
//     setFilterStatus("All");
//     setSelectedLocation("");
//     setStartDate(null);
//     setEndDate(null);
//     setCurrentPage(1);
//   };

//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4 flex flex-col h-full">
      
//       {/* === FILTERS === */}
//       <div className="flex items-center justify-end mb-4 gap-2 flex-wrap">
        
//         <select
//           value={filterStatus}
//           onChange={(e) => {
//             setFilterStatus(e.target.value);
//             setCurrentPage(1);
//           }}
//           className="border text-xs md:text-sm border-slate-200 rounded-lg px-2 py-1"
//         >
//           <option value="All">All Statuses</option>
//           {STATUSES.map((s) => (
//             <option key={s.value} value={s.value}>
//               {s.label}
//             </option>
//           ))}
//         </select>

//         <LocationFilter
//           locations={uniqueLocations}
//           selected={selectedLocation}
//           onChange={(loc) => {
//             setSelectedLocation(loc);
//             setCurrentPage(1);
//           }}
//         />

//         <DateRangePicker
//           startDate={startDate}
//           endDate={endDate}
//           onChange={(start, end) => {
//             setStartDate(start);
//             setEndDate(end);
//             setCurrentPage(1);
//           }}
//         />

//         <BlueButton onClick={resetFilters}>Reset Filters</BlueButton>
//         <RefreshButton onClick={fetchLeads} />
//       </div>

//       {/* === TABLE === */}
//       <div className="overflow-x-auto -mx-4 md:mx-0">
//         <table className="min-w-full text-left text-xs md:text-sm">
//           <thead>
//             <tr className="border-b border-slate-100 bg-slate-50">
//               <th className="py-2 px-2">Lead</th>
//               <th className="px-2">Owner</th>
//               <th className="px-2">Location</th>
//               <th className="px-2">Type</th>
//               <th className="px-2">Status</th>
//               <th className="px-2">Value</th>
//               <th className="px-2">Date</th>
//               <th className="px-2">Action</th>
//             </tr>
//           </thead>

//           <tbody>
//             {loading ? (
//               <tr>
//                 <td colSpan="8" className="py-6 text-center">
//                   <RegularLoader />
//                 </td>
//               </tr>
//             ) : paginatedLeads.length === 0 ? (
//               <tr>
//                 <td colSpan="8" className="py-6 text-center text-slate-400">
//                   No leads available.
//                 </td>
//               </tr>
//             ) : (
//               paginatedLeads.map((lead) => {
//                 const d = new Date(lead.createdAt);
//                 const monthLabel = monthLabels[d.getMonth()] || "—";

//                 return (
//                   <tr key={lead.id} className="border-b border-slate-50 last:border-0">
//                     <td className="px-2 py-2">{lead.name}</td>
//                     <td className="px-2">{lead.owner}</td>
//                     <td className="px-2">{lead.location}</td>
//                     <td className="px-2">{lead.type}</td>

//                     <td className="px-2">
//                       <span className="bg-blue-50 text-blue-700 border border-blue-100 px-2 py-1 rounded-full text-xs">
//                         {lead.status}
//                       </span>
//                     </td>

//                     <td className="px-2">{formatCurrency(lead.value)}</td>
//                     <td className="px-2">{monthLabel}</td>

//                     <td className="px-2 flex items-center gap-2">
//                       <button className="text-blue-600 hover:text-blue-800">
//                         <Eye size={16} />
//                       </button>

//                       <button className="text-amber-600 hover:text-amber-800">
//                         <Pencil size={16} />
//                       </button>
//                     </td>
//                   </tr>
//                 );
//               })
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* === PAGINATION === */}
//       {totalPages > 1 && (
//         <Pagination
//           currentPage={currentPage}
//           totalPages={totalPages}
//           onPageChange={setCurrentPage}
//         />
//       )}
//     </div>
//   );
// }





// import { useState, useMemo } from "react";

// import formatCurrency from "../../Component/EmployeeComponents/formatCurrency";
// import { monthLabels } from "../../Component/EmployeeComponents/monthLabels";
// import { STATUSES } from "../../Component/EmployeeComponents/STATUSES";
// import Pagination from "../../Component/Common/Pagination";
// import LocationFilter from "../../Component/Common/LocationFilter";
// import DateRangePicker from "../../Component/Common/DateRangePicker";
// import RefreshButton from "../../Component/Common/RefreshButton";


// export default function LeadsTable({ leads, filterStatus, setFilterStatus }) {
//   const [currentPage, setCurrentPage] = useState(1);
//   const [startDate, setStartDate] = useState(null);
//   const [endDate, setEndDate] = useState(null);
//   const [selectedLocation, setSelectedLocation] = useState("");

//   const pageSize = 7;

//   // Extract unique locations (e.g. city)
//   const uniqueLocations = [...new Set(leads.map((l) => l.location))];

//   // ✅ Filter logic
//   const filteredLeads = useMemo(() => {
//   return leads
//     .filter((lead) =>
//       filterStatus === "All" ? true : lead.status === filterStatus
//     )
//     .filter((lead) => {
//       if (!startDate || !endDate) return true;
//       const created = new Date(lead.createdAt);
//       return created >= startDate && created <= endDate;
//     })
//     .filter((lead) =>
//       selectedLocation ? lead.location === selectedLocation : true
//     );
// }, [leads, filterStatus, startDate, endDate, selectedLocation]);


//   const totalPages = Math.ceil(filteredLeads.length / pageSize);

//   const paginatedLeads = filteredLeads.slice(
//     (currentPage - 1) * pageSize,
//     currentPage * pageSize
//   );

//   // Reset page when filters change
//   const resetPage = () => setCurrentPage(1);

//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4 flex flex-col h-full">
      
//       {/* === Filters Row === */}
//       <div className="flex items-center justify-end mb-4 gap-3 flex-wrap">

//         {/* Status Filter */}
//         {/* Status Filter */}
//         <select
//           value={filterStatus}
//           onChange={(e) => {
//             setFilterStatus(e.target.value);
//             resetPage();
//           }}
//           className="text-xs md:text-sm border border-slate-200 rounded-lg px-2 py-1"
//         >
//           <option value="All">All Statuses</option>
//           {STATUSES.map((s) => (
//             <option key={s.value} value={s.value}>
//               {s.label}
//             </option>
//           ))}
//         </select>


//         {/* Location Filter */}
//         <LocationFilter
//           locations={uniqueLocations}
//           selected={selectedLocation}
//           onChange={(loc) => {
//             setSelectedLocation(loc);
//             resetPage();
//           }}
//         />

//         {/* Date Range */}
//         <DateRangePicker
//           startDate={startDate}
//           endDate={endDate}
//           onChange={(start, end) => {
//             setStartDate(start);
//             setEndDate(end);
//             resetPage();
//           }}
//         />
//         <RefreshButton />
//       </div>

//       {/* === Table === */}
//       <div className="overflow-x-auto -mx-4 md:mx-0">
//         <table className="min-w-full text-left text-xs md:text-sm">
//           <thead>
//             <tr className="border-b border-slate-100 bg-slate-50">
//               <th className="py-2 pl-4">Lead</th>
//               <th className="px-2">Owner</th>
//               <th className="px-2">Location</th>
//               <th className="px-2">Status</th>
//               <th className="px-2">Value</th>
//               <th className="px-2">Date</th>
//             </tr>
//           </thead>

//           <tbody>
//             {paginatedLeads.length === 0 && (
//               <tr>
//                 <td colSpan="5" className="py-6 text-center text-slate-400">
//                   No leads match your filters.
//                 </td>
//               </tr>
//             )}

//             {paginatedLeads.map((lead) => {
//               const d = new Date(lead.createdAt);
//               const monthLabel = monthLabels[d.getMonth()] || "—";

//               const statusColor =
//                 lead.status === "Won"
//                   ? "bg-green-50 text-green-700 border-green-100"
//                   : lead.status === "Lost"
//                   ? "bg-red-50 text-red-700 border-red-100"
//                   : lead.status === "In Progress"
//                   ? "bg-amber-50 text-amber-700 border-amber-100"
//                   : "bg-blue-50 text-blue-700 border-blue-100";

//               return (
//                 <tr key={lead.id} className="border-b border-slate-50 last:border-0">
//                   <td className="py-2 pl-4">{lead.name}</td>
//                   <td className="px-2">{lead.owner}</td>
//                   <td className="px-2">{lead.location}</td>
//                   <td className="px-2">
//                     <span className={`inline-flex px-2 py-0.5 rounded-full border text-xs ${statusColor}`}>
//                       {lead.status}
//                     </span>
//                   </td>
//                   <td className="px-2">{formatCurrency(lead.value)}</td>
//                   <td className="px-2">{monthLabel}</td>
//                 </tr>
//               );
//             })}
//           </tbody>
//         </table>
//       </div>

//       {/* === Pagination === */}
//       {totalPages > 1 && (
//         <Pagination
//           currentPage={currentPage}
//           totalPages={totalPages}
//           onPageChange={setCurrentPage}
//         />
//       )}
//     </div>
//   );
// }
